/*
 * The card and its rows.
 *
 * WHEN A CARD IS SHARED VERSUS PER-ROW is a spec decision, not a styling one,
 * and the components are separate so a screen states which it means. §10: in
 * Upcoming, rows carry no per-fencer data, so ONE card lists every registered
 * fencer with interior hairlines between names, because "card-per-name would be
 * border noise". In Active each fencer has a strip and a personal state, so each
 * gets its own card (§9's "hairline-bordered, name + personal state left, strip
 * at ~26px tabular right").
 */

import type { ReactNode } from "react";

import styles from "./Card.module.css";

export interface CardProps {
  children: ReactNode;

  /**
   * Squares the leading corners so the card sits flush beneath an attached event
   * header, the two reading as one object (decision 326). See
   * `EventSubHeaderProps.attached`, which is the other half — that header drops
   * its block-end border and this card's own block-start border becomes the seam.
   */
  attached?: boolean;

  /**
   * Halves the rows' padding.
   *
   * FOR ROWS THAT ARE NOT TARGETS. Upcoming and Past list a name and a club and
   * nothing in the row is clickable, so the 44px touch minimum — which exists to
   * size TARGETS — was buying height for no one and costing the density that
   * makes a tournament's entries scannable at a glance. Active keeps the default:
   * its rows carry a strip at depth zero and are read at arm's length on a phone,
   * which is the case the roomier spacing was written for.
   */
  dense?: boolean;

  /**
   * Drops the border, the radius and the fill, leaving the rows on the ground
   * beneath them.
   *
   * PROTOTYPE SCAFFOLDING for Active's layout variant `c` (roadmap item 4,
   * `lib/activeLayout.ts`), which asks what the screen reads like when an event
   * header and its fencers are one continuous block rather than a header
   * attached to a card. See `.cardFlush` for why the surface goes rather than
   * the element. Removed with the losing variants.
   */
  flush?: boolean;
}

/** A card. Holds one row (Active) or many (Upcoming, Past). */
export function Card({
  children,
  attached = false,
  dense = false,
  flush = false,
}: CardProps) {
  const cn = [
    styles.card,
    attached ? styles.cardAttached : "",
    dense ? styles.cardDense : "",
    flush ? styles.cardFlush : "",
  ]
    .filter(Boolean)
    .join(" ");
  return <div className={cn}>{children}</div>;
}

export interface CardRowProps {
  /** The row's leading content: a name, and any personal state beneath it. */
  children: ReactNode;

  /**
   * The trailing content: the wanted datum at depth zero (principle 2) — the
   * strip in Active, the placement in Past — or a capability pill.
   */
  trailing?: ReactNode;

  /**
   * Let the leading content span the whole row. Off by default, where the
   * leading column is only as wide as its content -- which is what lets a
   * trailing datum sit at the far edge. On for a row whose OWN content has
   * something to push to that edge: Upcoming's projection line, right-aligned
   * beside the name (decisions 550, 577).
   */
  fill?: boolean;

  /**
   * Let the trailing slot shrink to its widest piece and wrap inside itself,
   * end-aligned. Off by default, where the slot never shrinks (principle 2).
   * On for one case only: a split event's time on Upcoming's fencer axis
   * (decision 661), whose line is three unbreakable pieces and would otherwise
   * crush the event name to a letter per line on a phone.
   */
  trailingWraps?: boolean;

  /**
   * A class added to the row itself, for a view whose row needs one more rule
   * than the shared ones -- Active clips its rows so the P marker's enlarged
   * hit area cannot reach into the row above (post-Remenyik item 6).
   */
  className?: string;
}

/**
 * One row inside a card. Interior hairlines are applied by the stylesheet's
 * adjacent-sibling rule, so the first row never gets a top border and no caller
 * has to know which row it is rendering.
 */
export function CardRow({ children, trailing, fill = false, trailingWraps = false, className }: CardRowProps) {
  return (
    <div className={className ? `${styles.row} ${className}` : styles.row}>
      <div className={fill ? `${styles.rowMain} ${styles.rowMainFill}` : styles.rowMain}>{children}</div>
      {trailing ? (
        <div className={trailingWraps ? `${styles.rowTrailing} ${styles.rowTrailingWraps}` : styles.rowTrailing}>
          {trailing}
        </div>
      ) : null}
    </div>
  );
}
